import { useState, useId, useCallback, useEffect } from 'react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { ShieldCheck, CreditCard, CheckCircle2 } from 'lucide-react';
import { SlideOver } from '@/components/ui/SlideOver';
import { StepIndicator } from '@/components/ui/StepIndicator';
import { Button } from '@/components/ui/Button';
import { useCheckInBooking } from '@/hooks/api/useBookings';
import { useUpdateGuest } from '@/hooks/api/useGuests';
import { useRecordPayment } from '@/hooks/api/useFolio';
import type { BookingEntry } from '@/services/api/frontdesk.api';

// ─── Constants ───────────────────────────────────────────────────────────────

const STEPS = [
  { label: 'Guest Verification' },
  { label: 'Payment' },
  { label: 'Confirm' },
];

const ID_TYPES = [
  { value: '', label: 'Select ID type' },
  { value: 'passport', label: 'Passport' },
  { value: 'national_id', label: 'National ID' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'other', label: 'Other' },
] as const;

type PaymentMethodValue = typeof PAYMENT_METHODS[number]['value'];

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

function fmtCurrency(amount: number | string, currency = 'GBP'): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(num);
}

function nightsCount(checkIn: string, checkOut: string): number {
  try {
    return differenceInDays(parseISO(checkOut), parseISO(checkIn));
  } catch {
    return 0;
  }
}

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface IdDocumentForm {
  idType: string;
  idNumber: string;
  issuingCountry: string;
  expiryDate: string;
  idPhotoUrl: string;
}

interface PaymentForm {
  amount: string;
  method: PaymentMethodValue;
  reference: string;
  notes: string;
}

interface CheckInWizardProps {
  isOpen: boolean;
  onClose: () => void;
  booking: BookingEntry;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CheckInWizard({ isOpen, onClose, booking }: CheckInWizardProps) {
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Form state
  const [idDoc, setIdDoc] = useState<IdDocumentForm>({
    idType: '',
    idNumber: '',
    issuingCountry: '',
    expiryDate: '',
    idPhotoUrl: '',
  });

  const [payment, setPayment] = useState<PaymentForm>({
    amount: booking.totalAmount ?? '0',
    method: 'cash',
    reference: '',
    notes: '',
  });

  const [skipPayment, setSkipPayment] = useState(false);
  // Actual check-in datetime — defaults to now; can be set to a past time for backdating
  const [actualCheckInDateTime, setActualCheckInDateTime] = useState(
    () => format(new Date(), "yyyy-MM-dd'T'HH:mm"),
  );

  // IDs for accessibility
  const idTypeId = useId();
  const idNumberId = useId();
  const issuingCountryId = useId();
  const expiryDateId = useId();
  const idPhotoId = useId();
  const payAmountId = useId();
  const payMethodId = useId();
  const payRefId = useId();
  const payNotesId = useId();

  // Mutations
  const checkInMutation = useCheckInBooking();
  const updateGuestMutation = useUpdateGuest();
  const paymentMutation = useRecordPayment();

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setError('');
      setSuccessMessage('');
      setSkipPayment(false);
      setActualCheckInDateTime(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
      setIdDoc({
        idType: '',
        idNumber: '',
        issuingCountry: '',
        expiryDate: '',
        idPhotoUrl: '',
      });
      setPayment({
        amount: booking.totalAmount ?? '0',
        method: 'cash',
        reference: '',
        notes: '',
      });
    }
  }, [isOpen, booking.totalAmount]);

  const guestName = booking.guest?.fullName ?? 'Unknown Guest';
  const guestEmail = booking.guest?.email ?? '';
  const guestPhone = booking.guest?.phone ?? '';
  const unitName = booking.unit?.name ?? booking.unitId.slice(0, 8);
  const nights = nightsCount(booking.checkIn, booking.checkOut);
  const totalAmount = parseFloat(booking.totalAmount) || 0;

  // ─── Keyboard navigation ─────────────────────────────────────────────────

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Let forms handle Enter naturally
    }
  }, []);

  // ─── Step navigation ──────────────────────────────────────────────────────

  const isStep1Valid = idDoc.idType && idDoc.idNumber;

  const goToStep2 = () => {
    if (!isStep1Valid) {
      setError('Please provide ID type and number.');
      return;
    }
    setError('');
    setStep(1);
  };

  const goToStep3 = () => {
    setError('');
    setStep(2);
  };

  const handleComplete = async () => {
    setError('');
    try {
      // 1. Record payment if not skipped
      if (!skipPayment && parseFloat(payment.amount) > 0) {
        const payPayload: Parameters<typeof paymentMutation.mutateAsync>[0] = {
          bookingId: booking.id,
          amount: parseFloat(payment.amount),
          paymentMethod: payment.method,
        };
        if (payment.reference) payPayload.reference = payment.reference;
        if (payment.notes) payPayload.notes = payment.notes;
        await paymentMutation.mutateAsync(payPayload);
      }

      // 2. Update guest with ID info
      if (booking.guestId && idDoc.idType) {
        const guestPayload: Parameters<typeof updateGuestMutation.mutateAsync>[0]['payload'] = {
          idType: idDoc.idType,
        };
        if (idDoc.idNumber) guestPayload.idNumber = idDoc.idNumber;
        if (idDoc.issuingCountry) guestPayload.idIssuingCountry = idDoc.issuingCountry;
        if (idDoc.expiryDate) guestPayload.idExpiryDate = idDoc.expiryDate;
        await updateGuestMutation.mutateAsync({
          id: booking.guestId,
          payload: guestPayload,
        });
      }

      // 3. Check in
      const checkInPayload: Parameters<typeof checkInMutation.mutateAsync>[0] = {
        id: booking.id,
        actualCheckInTime: new Date(actualCheckInDateTime).toISOString(),
      };
      if (idDoc.idPhotoUrl) checkInPayload.idDocumentUrl = idDoc.idPhotoUrl;
      await checkInMutation.mutateAsync(checkInPayload);

      setSuccessMessage('Guest checked in successfully.');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Check-in failed. Please try again.';
      setError(message);
    }
  };

  const isProcessing = checkInMutation.isPending || updateGuestMutation.isPending || paymentMutation.isPending;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <SlideOver isOpen={isOpen} onClose={onClose} title="Check-in Wizard" width="max-w-xl">
      <div onKeyDown={handleKeyDown}>
        <StepIndicator steps={STEPS} currentStep={step} />

        {/* Success message */}
        {successMessage && (
          <div className="mb-4 rounded-lg bg-green-50 border border-green-200 p-3 flex items-center gap-2" role="status" aria-live="polite">
            <CheckCircle2 size={16} className="text-green-600 shrink-0" aria-hidden="true" />
            <p className="text-sm font-medium text-green-700">{successMessage}</p>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3" role="alert">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* ─── Step 1: Guest Verification ───────────────────────────────────── */}
        {step === 0 && (
          <div className="space-y-5">
            {/* Guest info (read-only) */}
            <section className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <ShieldCheck size={16} className="text-primary-600" aria-hidden="true" />
                Guest Information
              </h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-gray-500">Name</dt>
                  <dd className="font-medium text-gray-900">{guestName}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Email</dt>
                  <dd className="text-gray-700">{guestEmail || '--'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Phone</dt>
                  <dd className="text-gray-700">{guestPhone || '--'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Unit</dt>
                  <dd className="font-medium text-gray-900">{unitName}</dd>
                </div>
              </dl>
            </section>

            {/* ID Document section */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">ID Document</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor={idTypeId} className="block text-sm font-medium text-gray-700 mb-1">
                    ID Type <span className="text-gray-400 font-normal">(required)</span>
                  </label>
                  <select
                    id={idTypeId}
                    value={idDoc.idType}
                    onChange={(e) => setIdDoc((prev) => ({ ...prev, idType: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {ID_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor={idNumberId} className="block text-sm font-medium text-gray-700 mb-1">
                    ID Number <span className="text-gray-400 font-normal">(required)</span>
                  </label>
                  <input
                    id={idNumberId}
                    type="text"
                    value={idDoc.idNumber}
                    onChange={(e) => setIdDoc((prev) => ({ ...prev, idNumber: e.target.value }))}
                    placeholder="e.g. AB1234567"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={issuingCountryId} className="block text-sm font-medium text-gray-700 mb-1">
                      Issuing Country
                    </label>
                    <input
                      id={issuingCountryId}
                      type="text"
                      value={idDoc.issuingCountry}
                      onChange={(e) => setIdDoc((prev) => ({ ...prev, issuingCountry: e.target.value }))}
                      placeholder="e.g. GB"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label htmlFor={expiryDateId} className="block text-sm font-medium text-gray-700 mb-1">
                      Expiry Date
                    </label>
                    <input
                      id={expiryDateId}
                      type="date"
                      value={idDoc.expiryDate}
                      onChange={(e) => setIdDoc((prev) => ({ ...prev, expiryDate: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor={idPhotoId} className="block text-sm font-medium text-gray-700 mb-1">
                    ID Photo URL
                  </label>
                  <input
                    id={idPhotoId}
                    type="url"
                    value={idDoc.idPhotoUrl}
                    onChange={(e) => setIdDoc((prev) => ({ ...prev, idPhotoUrl: e.target.value }))}
                    placeholder="https://..."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Camera capture coming soon</p>
                </div>
              </div>
            </section>

            <div className="pt-2">
              <Button
                type="button"
                fullWidth
                onClick={goToStep2}
                disabled={!isStep1Valid}
              >
                Verify & Continue
              </Button>
            </div>
          </div>
        )}

        {/* ─── Step 2: Payment ──────────────────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-5">
            {/* Booking summary */}
            <section className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <CreditCard size={16} className="text-primary-600" aria-hidden="true" />
                Booking Summary
              </h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Unit</dt>
                  <dd className="font-medium text-gray-900">{unitName}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Dates</dt>
                  <dd className="text-gray-700">{fmtDate(booking.checkIn)} - {fmtDate(booking.checkOut)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Nights</dt>
                  <dd className="text-gray-700">{nights}</dd>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
                  <dt className="font-semibold text-gray-900">Total</dt>
                  <dd className="font-bold text-gray-900">{fmtCurrency(totalAmount, booking.currency)}</dd>
                </div>
              </dl>
            </section>

            {/* Outstanding balance */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm">
                <span className="font-medium text-amber-800">Outstanding Balance: </span>
                <span className="font-bold text-amber-900">{fmtCurrency(totalAmount, booking.currency)}</span>
              </p>
            </div>

            {/* Payment form */}
            {!skipPayment && (
              <section className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700">Record Payment</h3>

                <div>
                  <label htmlFor={payAmountId} className="block text-sm font-medium text-gray-700 mb-1">
                    Amount <span className="text-gray-400 font-normal">(required)</span>
                  </label>
                  <input
                    id={payAmountId}
                    type="number"
                    min={0}
                    step="0.01"
                    value={payment.amount}
                    onChange={(e) => setPayment((prev) => ({ ...prev, amount: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor={payMethodId} className="block text-sm font-medium text-gray-700 mb-1">
                    Method <span className="text-gray-400 font-normal">(required)</span>
                  </label>
                  <select
                    id={payMethodId}
                    value={payment.method}
                    onChange={(e) => setPayment((prev) => ({ ...prev, method: e.target.value as PaymentMethodValue }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor={payRefId} className="block text-sm font-medium text-gray-700 mb-1">
                    Reference
                  </label>
                  <input
                    id={payRefId}
                    type="text"
                    value={payment.reference}
                    onChange={(e) => setPayment((prev) => ({ ...prev, reference: e.target.value }))}
                    placeholder="Receipt number, last 4 digits..."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor={payNotesId} className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    id={payNotesId}
                    value={payment.notes}
                    onChange={(e) => setPayment((prev) => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                    placeholder="Optional notes..."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  />
                </div>
              </section>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => setStep(0)}
              >
                Back
              </Button>
              <Button
                type="button"
                variant={skipPayment ? 'secondary' : 'primary'}
                className="flex-1"
                onClick={() => {
                  setSkipPayment(!skipPayment);
                }}
              >
                {skipPayment ? 'Enter Payment' : 'Skip -- Pay at Checkout'}
              </Button>
              <Button
                type="button"
                className="flex-1"
                onClick={goToStep3}
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* ─── Step 3: Confirm ──────────────────────────────────────────────── */}
        {step === 2 && (
          <div className="space-y-5">
            <section className="bg-gray-50 rounded-lg p-4 space-y-4">
              <h3 className="text-sm font-semibold text-gray-700">Check-in Summary</h3>

              {/* Guest */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Guest</p>
                <p className="text-sm font-medium text-gray-900">{guestName}</p>
                {guestEmail && <p className="text-xs text-gray-500">{guestEmail}</p>}
              </div>

              {/* ID Verification */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">ID Verification</p>
                <div className="flex items-center gap-2">
                  <ShieldCheck size={14} className="text-green-600" aria-hidden="true" />
                  <span className="text-sm text-green-700 font-medium">
                    {ID_TYPES.find((t) => t.value === idDoc.idType)?.label ?? idDoc.idType}
                    {idDoc.idNumber && ` -- ${idDoc.idNumber}`}
                  </span>
                </div>
                {idDoc.issuingCountry && (
                  <p className="text-xs text-gray-500 ml-6">Country: {idDoc.issuingCountry}</p>
                )}
              </div>

              {/* Payment status */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Payment</p>
                {skipPayment ? (
                  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
                    Pay at Checkout
                  </span>
                ) : parseFloat(payment.amount) >= totalAmount ? (
                  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-700">
                    Paid -- {fmtCurrency(payment.amount, booking.currency)} via {PAYMENT_METHODS.find((m) => m.value === payment.method)?.label}
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700">
                    Partial -- {fmtCurrency(payment.amount, booking.currency)} of {fmtCurrency(totalAmount, booking.currency)}
                  </span>
                )}
              </div>

              {/* Room assignment */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Room</p>
                <p className="text-sm font-medium text-gray-900">{unitName}</p>
                <p className="text-xs text-gray-500">
                  {fmtDate(booking.checkIn)} - {fmtDate(booking.checkOut)} ({nights} nights)
                </p>
              </div>

              {/* Actual check-in datetime — supports backdating */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Actual Check-in Date &amp; Time
                </p>
                <input
                  type="datetime-local"
                  value={actualCheckInDateTime}
                  max={format(new Date(), "yyyy-MM-dd'T'HH:mm")}
                  onChange={(e) => setActualCheckInDateTime(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {actualCheckInDateTime < format(new Date(), "yyyy-MM-dd'T'HH:mm").slice(0, 16) && (
                  <p className="mt-1 text-xs text-amber-600">
                    ⚡ Backdated check-in — actual time will be recorded as selected
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-400">
                  Defaults to now. Change only if the guest arrived earlier.
                </p>
              </div>

              {/* Special requests */}
              {booking.specialRequests && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-amber-800 uppercase tracking-wide mb-1">Special Requests</p>
                  <p className="text-sm text-amber-900">{booking.specialRequests}</p>
                </div>
              )}
            </section>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => setStep(1)}
              >
                Back
              </Button>
              <Button
                type="button"
                className="flex-1"
                onClick={handleComplete}
                isLoading={isProcessing}
                disabled={isProcessing}
              >
                <CheckCircle2 size={16} aria-hidden="true" />
                Complete Check-in
              </Button>
            </div>
          </div>
        )}
      </div>
    </SlideOver>
  );
}
